import { useEffect, useState, useCallback, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import api from "../services/api";
import { useNavigate } from "react-router-dom";

const C = {
  green:"#2D5A3D", greenMid:"#4A7C59", greenL:"#EAF4EE",
  gold:"#B86A10",  goldL:"#E8922A",    goldBg:"#FDF0D5",
  terra:"#B85C38",
  ink:"#2C1810",   inkMid:"#5C3D2E",   inkLight:"#9A7B60",
  cream:"#FDFAF3", creamD:"#F5EDD8",
};


/* ─── Skeleton shimmer components ─────────────────────────────────────── */

/*
 * A single shimmer bar.  width/height/borderRadius are caller-supplied so
 * the same primitive covers every shape in the design (text lines, avatars,
 * colour-coded left-borders, action buttons).
 */
function Shimmer({ w = "100%", h = 14, r = 6, style = {} }) {
  return (
    <div style={{
      width: w, height: h, borderRadius: r,
      background: "linear-gradient(90deg, #F5EDD8 25%, #FDF8F0 50%, #F5EDD8 75%)",
      backgroundSize: "200% 100%",
      animation: "shimmer 1.4s ease-in-out infinite",
      ...style,
    }} />
  );
}

/*
 * One skeleton PatientCard — mirrors the real PatientCard geometry:
 *   • coloured left-border stripe (placeholder colour #E2E8F0)
 *   • name line + meta line top-left, patient-ID pill top-right
 *   • three action-button outlines at the bottom
 */
function SkeletonPatientCard() {
  return (
    <div style={{
      background: "#fff", borderRadius: 16, padding: 16,
      boxShadow: "0 4px 16px rgba(0,0,0,0.07)",
      border: "1px solid #F5EDD8",
      borderLeft: "4px solid #E2E8F0",
    }}>
      <style>{`
        @keyframes shimmer {
          0%   { background-position: 200% 0; }
          100% { background-position: -200% 0; }
        }
      `}</style>

      {/* top row: name block left, ID pill right */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 14 }}>
        <div style={{ display: "flex", flexDirection: "column", gap: 7, flex: 1, marginRight: 16 }}>
          <Shimmer w="55%" h={15} r={6} />
          <Shimmer w="35%" h={11} r={5} />
          <Shimmer w="28%" h={10} r={5} />
        </div>
        <Shimmer w={72} h={20} r={6} />
      </div>

      {/* action buttons row */}
      <div style={{ display: "flex", gap: 8 }}>
        <Shimmer w="auto" h={34} r={10} style={{ flex: 1 }} />
        <Shimmer w="auto" h={34} r={10} style={{ flex: 1 }} />
        <Shimmer w={40}   h={34} r={10} />
      </div>
    </div>
  );
}

/*
 * A labelled section of skeleton cards — mirrors the real Pending /
 * In-Progress / Finished sections: dot + uppercase label, then a
 * responsive grid of SkeletonPatientCards.
 */
function SkeletonSection({ dotColor = "#E2E8F0", count = 3 }) {
  return (
    <div>
      {/* section label */}
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
        <span style={{ width: 10, height: 10, borderRadius: "50%", background: dotColor, display: "inline-block" }} />
        <Shimmer w={120} h={11} r={4} />
      </div>

      {/* card grid */}
      <div style={{ display: "grid", gap: 16, gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))" }}>
        {Array.from({ length: count }).map((_, i) => (
          <SkeletonPatientCard key={i} />
        ))}
      </div>
    </div>
  );
}

function StatCard({ icon, value, label, sub, color, delay=0, onClick }) {
  const isClickable = !!onClick;
  return (
    <motion.div
      initial={{opacity:0,y:12}} animate={{opacity:1,y:0}} transition={{delay}}
      onClick={onClick}
      whileHover={isClickable ? {y:-2, boxShadow:"0 8px 28px rgba(0,0,0,0.11)"} : {}}
      style={{
        background:"#fff", borderRadius:16, padding:"18px 20px",
        boxShadow:"0 4px 20px rgba(0,0,0,0.07)", border:`1px solid ${C.creamD}`,
        borderLeft:`4px solid ${color}`,
        display:"flex", alignItems:"center", gap:14, flex:1, minWidth:140,
        cursor: isClickable ? "pointer" : "default",
        transition:"box-shadow 0.15s",
        outline:"none",
      }}
      role={isClickable ? "button" : undefined}
      tabIndex={isClickable ? 0 : undefined}
      onKeyDown={isClickable ? (e) => { if (e.key === "Enter" || e.key === " ") onClick(); } : undefined}
    >
      <div style={{
        width:44, height:44, borderRadius:12, flexShrink:0,
        background:color+"18", display:"flex", alignItems:"center",
        justifyContent:"center", fontSize:20,
      }}>{icon}</div>
      <div style={{flex:1, minWidth:0}}>
        <div style={{fontSize:26,fontWeight:800,color:C.ink,lineHeight:1,fontFamily:"'Playfair Display',serif"}}>
          {value ?? "—"}
        </div>
        <div style={{fontSize:12,fontWeight:600,color:C.inkMid,marginTop:2,fontFamily:"'Inter',sans-serif"}}>
          {label}
        </div>
        {sub && <div style={{fontSize:11,color:C.inkLight,marginTop:1,fontFamily:"'Inter',sans-serif"}}>{sub}</div>}
        {isClickable && (
          <div style={{fontSize:10,color:color,marginTop:3,fontFamily:"'Inter',sans-serif",opacity:0.75}}>
            View all →
          </div>
        )}
      </div>
    </motion.div>
  );
}

function OnlineCard({ appt, index }) {
  const timeStr = appt.preferredTime ? (() => {
    const [h,m] = appt.preferredTime.split(":").map(Number);
    return `${h%12||12}:${String(m).padStart(2,"0")} ${h>=12?"PM":"AM"}`;
  })() : "Time TBD";

  const statusColor = {
    Pending:   {bg:"#FEF3C7",text:"#92400E"},
    Confirmed: {bg:"#D1FAE5",text:"#065F46"},
    Completed: {bg:"#E0E7FF",text:"#3730A3"},
  }[appt.status] || {bg:C.creamD,text:C.inkMid};

  return (
    <motion.div initial={{opacity:0,x:-10}} animate={{opacity:1,x:0}} transition={{delay:index*0.05}}
      style={{
        background:"#fff", borderRadius:14, padding:"14px 16px",
        border:`1px solid ${C.creamD}`, borderLeft:`4px solid ${C.goldL}`,
        display:"flex", alignItems:"center", gap:14,
        boxShadow:"0 2px 10px rgba(0,0,0,0.05)",
      }}>
      <div style={{
        minWidth:56, textAlign:"center", background:C.goldBg,
        borderRadius:10, padding:"8px 6px", border:`1px solid ${C.goldL}30`,
      }}>
        <div style={{fontSize:13,fontWeight:800,color:C.gold,fontFamily:"'Inter',sans-serif",lineHeight:1}}>
          {timeStr.split(" ")[0]}
        </div>
        <div style={{fontSize:10,color:C.gold,fontFamily:"'Inter',sans-serif"}}>{timeStr.split(" ")[1]}</div>
      </div>

      <div style={{flex:1,minWidth:0}}>
        <div style={{fontWeight:700,color:C.ink,fontSize:14,fontFamily:"'Inter',sans-serif",
          whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>
          {appt.name}
        </div>
        <div style={{fontSize:12,color:C.inkLight,fontFamily:"'Inter',sans-serif",marginTop:2}}>
          {appt.consultationType||"Online Video"} · {appt.phone}
        </div>
        {appt.message && (
          <div style={{fontSize:11,color:C.inkLight,marginTop:3,fontFamily:"'Merriweather',serif",
            whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>
            "{appt.message}"
          </div>
        )}
      </div>

      <div style={{
        fontSize:11,fontWeight:700,fontFamily:"'Inter',sans-serif",
        padding:"4px 10px",borderRadius:20,whiteSpace:"nowrap",flexShrink:0,
        background:statusColor.bg, color:statusColor.text,
      }}>
        {appt.status}
      </div>
    </motion.div>
  );
}

function PatientCard({ patient, index, status, onConsult, onHistory, onEdit }) {
  const isInProgress = status === "in_progress";
  const isCompleted  = status === "completed";

  const borderColor = isCompleted ? C.greenMid : isInProgress ? "#F59E0B" : "#3B82F6";
  const cardOpacity = isCompleted ? 0.75 : 1;

  const statusBadge = isCompleted
    ? { label: "✓ Finished", color: C.greenMid }
    : isInProgress
    ? { label: "✍️ In Progress", color: "#D97706" }
    : null;

  const btnLabel = isCompleted ? "Edit RX" : isInProgress ? "Continue RX" : "Start Consultation";
  const btnBg    = isCompleted ? C.creamD : isInProgress ? "#FEF3C7" : "#3B82F6";
  const btnColor = isCompleted ? C.inkMid : isInProgress ? "#92400E" : "#fff";

  return (
    <motion.div initial={{opacity:0,y:10}} animate={{opacity:1,y:0}} transition={{delay:index*0.04}}
      style={{
        background:"#fff", borderRadius:16, padding:16,
        boxShadow:"0 4px 16px rgba(0,0,0,0.07)", border:`1px solid ${C.creamD}`,
        borderLeft:`4px solid ${borderColor}`,
        opacity: cardOpacity,
      }}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:10}}>
        <div>
          <div style={{fontWeight:700,color:C.ink,fontSize:15,fontFamily:"'Inter',sans-serif"}}>{patient.name}</div>
          <div style={{fontSize:12,color:C.inkLight,marginTop:2,fontFamily:"'Inter',sans-serif"}}>
            {patient.age ? `${patient.age} yrs · ` : ""}{patient.gender}
          </div>
          {patient.phone && (
            <div style={{fontSize:11,color:C.inkLight,marginTop:1,fontFamily:"'Inter',sans-serif"}}>{patient.phone}</div>
          )}
        </div>
        <div style={{textAlign:"right"}}>
          <span style={{fontSize:11,fontFamily:"monospace",background:C.creamD,color:C.inkMid,padding:"2px 8px",borderRadius:6}}>
            {patient.patientId}
          </span>
          {statusBadge && (
            <div style={{fontSize:11,color:statusBadge.color,marginTop:4,fontWeight:600,fontFamily:"'Inter',sans-serif"}}>
              {statusBadge.label}
            </div>
          )}
        </div>
      </div>

      <div style={{display:"flex",gap:8}}>
        <button onClick={onConsult} style={{
          flex:1, padding:"8px 0", borderRadius:10, border:"none",
          fontWeight:600, fontSize:13, cursor:"pointer", fontFamily:"'Inter',sans-serif",
          background: btnBg,
          color: btnColor,
        }}>
          {btnLabel}
        </button>
        <button onClick={onHistory} style={{
          flex:1, padding:"8px 0", borderRadius:10, border:"none",
          fontWeight:600, fontSize:13, cursor:"pointer", fontFamily:"'Inter',sans-serif",
          background:C.creamD, color:C.inkMid,
        }}>
          History
        </button>
        <button onClick={onEdit} title="Edit patient" style={{
          padding:"8px 12px", borderRadius:10,
          border:`1px solid #BFDBFE`, background:"#EFF6FF",
          color:"#3B82F6", fontSize:14, cursor:"pointer",
        }}>✏️</button>
      </div>
    </motion.div>
  );
}

/* ─── Follow-Up Banner ─────────────────────────────────────────────────── */
/*
 * Collapsible amber panel shown when the doctor has patients with a
 * follow-up date of today.  Dismissed state survives navigation within
 * the session (sessionStorage key keyed to today's date so it reappears
 * tomorrow automatically).
 */
const FU_DISMISS_KEY = `fuDismissed_${new Date().toISOString().slice(0, 10)}`;

function FollowUpCard({ fu, onCheckIn, checkingIn }) {
  return (
    <div style={{
      background: "#fff",
      borderRadius: 14,
      border: "1px solid #FED7AA",
      borderTop: "3px solid #F97316",
      padding: "14px 16px",
      minWidth: 220,
      maxWidth: 260,
      flexShrink: 0,
      display: "flex",
      flexDirection: "column",
      gap: 8,
      boxShadow: "0 2px 10px rgba(249,115,22,0.10)",
    }}>
      {/* Name + ID */}
      <div>
        <div style={{
          fontWeight: 700, fontSize: 14, color: "#7C2D12",
          fontFamily: "'Inter',sans-serif",
          whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
        }}>
          {fu.name}
        </div>
        <div style={{ display: "flex", gap: 6, marginTop: 3, flexWrap: "wrap" }}>
          <span style={{
            fontSize: 10, fontFamily: "monospace",
            background: "#FEF3C7", color: "#92400E",
            padding: "1px 7px", borderRadius: 5,
          }}>
            {fu.patientId}
          </span>
          {fu.age && (
            <span style={{ fontSize: 11, color: "#9A3412", fontFamily: "'Inter',sans-serif" }}>
              {fu.age} yrs
            </span>
          )}
          {fu.gender && (
            <span style={{ fontSize: 11, color: "#9A3412", fontFamily: "'Inter',sans-serif" }}>
              · {fu.gender}
            </span>
          )}
        </div>
      </div>

      {/* Follow-up note */}
      {fu.followUpNote ? (
        <div style={{
          fontSize: 12, color: "#B45309", fontFamily: "'Merriweather',serif",
          fontStyle: "italic", lineHeight: 1.4,
          display: "-webkit-box", WebkitLineClamp: 2,
          WebkitBoxOrient: "vertical", overflow: "hidden",
        }}>
          "{fu.followUpNote}"
        </div>
      ) : (
        <div style={{ fontSize: 11, color: "#D97706", fontFamily: "'Inter',sans-serif" }}>
          No note recorded
        </div>
      )}

      {/* Phone */}
      {fu.phone && (
        <div style={{ fontSize: 11, color: "#9A3412", fontFamily: "'Inter',sans-serif" }}>
          📞 {fu.phone}
        </div>
      )}

      {/* Check-In button */}
      <button
        onClick={() => onCheckIn(fu)}
        disabled={checkingIn}
        style={{
          marginTop: "auto",
          padding: "7px 0", borderRadius: 9, border: "none",
          background: checkingIn ? "#FED7AA" : "#EA580C",
          color: "#fff", fontWeight: 700, fontSize: 12,
          cursor: checkingIn ? "default" : "pointer",
          fontFamily: "'Inter',sans-serif",
          transition: "background 0.15s",
        }}
      >
        {checkingIn ? "Adding…" : "✓ Check In"}
      </button>
    </div>
  );
}

function FollowUpBanner({ followUps, onCheckIn, checkingInId }) {
  const [dismissed, setDismissed] = useState(
    () => sessionStorage.getItem(FU_DISMISS_KEY) === "1"
  );
  const [expanded, setExpanded] = useState(true);

  if (dismissed || followUps.length === 0) return null;

  const handleDismiss = () => {
    sessionStorage.setItem(FU_DISMISS_KEY, "1");
    setDismissed(true);
  };

  const n = followUps.length;

  return (
    <motion.div
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -6 }}
      style={{
        background: "linear-gradient(135deg,#FFF7ED,#FFFBEB)",
        border: "1px solid #FDBA74",
        borderLeft: "5px solid #EA580C",
        borderRadius: 16,
        overflow: "hidden",
        boxShadow: "0 4px 20px rgba(234,88,12,0.11)",
      }}
    >
      {/* ── Banner header row ── */}
      <div style={{
        display: "flex", alignItems: "center", gap: 12,
        padding: "13px 18px",
        borderBottom: expanded ? "1px solid #FED7AA" : "none",
      }}>
        <span style={{ fontSize: 20, flexShrink: 0 }}>🔔</span>

        <div style={{ flex: 1, minWidth: 0 }}>
          <span style={{
            fontWeight: 700, fontSize: 13, color: "#9A3412",
            fontFamily: "'Inter',sans-serif",
          }}>
            {n} follow-up patient{n > 1 ? "s" : ""} today
          </span>
          <span style={{
            marginLeft: 8, fontSize: 11, color: "#B45309",
            fontFamily: "'Inter',sans-serif",
          }}>
            — scheduled by you for today's visit
          </span>
        </div>

        {/* Collapse toggle */}
        <button
          onClick={() => setExpanded(v => !v)}
          style={{
            padding: "4px 12px", borderRadius: 8,
            border: "1px solid #FDBA74", background: "transparent",
            color: "#C2410C", fontWeight: 600, fontSize: 12,
            cursor: "pointer", fontFamily: "'Inter',sans-serif",
            flexShrink: 0,
          }}
        >
          {expanded ? "Hide ▲" : "Show ▼"}
        </button>

        {/* Dismiss for session */}
        <button
          onClick={handleDismiss}
          title="Dismiss for this session"
          style={{
            background: "transparent", border: "none", cursor: "pointer",
            color: "#EA580C", fontSize: 20, lineHeight: 1,
            padding: "0 2px", flexShrink: 0,
          }}
        >
          ×
        </button>
      </div>

      {/* ── Expandable card strip ── */}
      <AnimatePresence initial={false}>
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.22 }}
            style={{ overflow: "hidden" }}
          >
            <div style={{
              padding: "16px 18px",
              overflowX: "auto",
              display: "flex",
              gap: 14,
              /* hide scrollbar but keep scroll */
              scrollbarWidth: "thin",
              scrollbarColor: "#FDBA74 transparent",
            }}>
              {followUps.map(fu => (
                <FollowUpCard
                  key={fu._id || fu.patientId}
                  fu={fu}
                  onCheckIn={onCheckIn}
                  checkingIn={checkingInId === (fu._id || fu.patientId)}
                />
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

export default function DoctorDashboard() {
  const [patients,    setPatients]    = useState([]);
  const [stats,       setStats]       = useState(null);
  const [onlineAppts, setOnlineAppts] = useState([]);
  const [followUps,   setFollowUps]   = useState([]);
  const [checkingInId, setCheckingInId] = useState(null);
  const [loading,     setLoading]     = useState(true);
  const [showOnline,  setShowOnline]  = useState(true);
  const navigate = useNavigate();

  // Read doctor identity from sessionStorage (set at login)
  const doctorUser = useMemo(() => {
    try { return JSON.parse(sessionStorage.getItem("doctorUser") || "null"); }
    catch { return null; }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [pRes, sRes, oRes, fRes] = await Promise.allSettled([
        api.get("/patients/today"),
        api.get("/patients/stats/summary"),
        api.get("/requests/online/today"),
        api.get("/patients/followups/today"),
      ]);
      if (pRes.status === "fulfilled") setPatients(pRes.value.data || []);
      if (sRes.status === "fulfilled") setStats(sRes.value.data);
      if (oRes.status === "fulfilled") setOnlineAppts(oRes.value.data || []);
      if (fRes.status === "fulfilled") setFollowUps(fRes.value.data || []);
    } catch(err) {
      console.error("Dashboard load error:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleCheckIn = useCallback(async (fu) => {
    const key = fu._id || fu.patientId;
    setCheckingInId(key);
    try {
      await api.post(`/patients/${fu.patientId}/checkin`);
      // Remove from follow-up list so the card disappears immediately
      setFollowUps(prev => prev.filter(f => (f._id || f.patientId) !== key));
      // Reload queue so the patient appears in Pending
      load();
    } catch (err) {
      console.error("Follow-up check-in failed:", err);
    } finally {
      setCheckingInId(null);
    }
  }, [load]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const t = setInterval(load, 3 * 60 * 1000);
    return () => clearInterval(t);
  }, [load]);

  const pending    = patients.filter(p => !p.consultationStatus || p.consultationStatus === "pending");
  const inProgress = patients.filter(p => p.consultationStatus === "in_progress");
  const completed  = patients.filter(p => p.consultationStatus === "completed" || (p.consulted && p.consultationStatus !== "in_progress"));

  const todayLabel = new Date().toLocaleDateString("en-IN", {
    weekday:"long", day:"numeric", month:"long",
  });

  return (
    <div style={{display:"flex",flexDirection:"column",gap:24}}>

      {/* Header */}
      <motion.div initial={{opacity:0,y:-10}} animate={{opacity:1,y:0}}
        style={{display:"flex",alignItems:"flex-start",justifyContent:"space-between",flexWrap:"wrap",gap:12}}>
        <div>
          <div style={{display:"flex",alignItems:"center",gap:10,flexWrap:"wrap",marginBottom:4}}>
            <h1 style={{fontSize:22,fontWeight:800,color:C.ink,fontFamily:"'Playfair Display',serif",margin:0}}>
              OPD Dashboard
            </h1>
            {/* Doctor name badge — visually confirms whose session this is */}
            {doctorUser?.name && (
              <span style={{
                fontSize:13,fontWeight:700,fontFamily:"'Inter',sans-serif",
                background:"#EFF6FF",color:"#1D4ED8",
                border:"1px solid #BFDBFE",
                padding:"3px 12px",borderRadius:20,
              }}>
                🩺 {doctorUser.name}
              </span>
            )}
          </div>
          <p style={{fontSize:13,color:C.inkLight,fontFamily:"'Inter',sans-serif",margin:0}}>
            {todayLabel} · {pending.length} pending · {inProgress.length} in progress · {completed.length} finished
            <span style={{marginLeft:8,fontSize:11,color:"#6B7280"}}>
              — Only your patients are shown
            </span>
          </p>
        </div>
        <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
          <button onClick={() => navigate("/staff-portal/patient/new")}
            style={{padding:"9px 16px",borderRadius:10,border:"none",background:C.greenMid,
              color:"#fff",fontWeight:600,fontSize:13,cursor:"pointer",fontFamily:"'Inter',sans-serif"}}>
            + New Patient
          </button>
          <button onClick={() => navigate("/staff-portal/patient/search")}
            style={{padding:"9px 16px",borderRadius:10,border:`1px solid ${C.creamD}`,
              background:"#fff",color:C.inkMid,fontWeight:600,fontSize:13,cursor:"pointer",fontFamily:"'Inter',sans-serif"}}>
            🔍 Old Patient
          </button>
          <button onClick={load} title="Refresh"
            style={{padding:"9px 14px",borderRadius:10,border:`1px solid ${C.creamD}`,
              background:"#fff",color:C.inkMid,fontSize:14,cursor:"pointer"}}>
            ↻
          </button>
        </div>
      </motion.div>

      {/* Follow-Up Banner */}
      <AnimatePresence>
        {!loading && (
          <FollowUpBanner
            followUps={followUps}
            onCheckIn={handleCheckIn}
            checkingInId={checkingInId}
          />
        )}
      </AnimatePresence>

      {/* Stats Bar */}
      <div style={{display:"flex",gap:12,flexWrap:"wrap"}}>
        <StatCard icon="🏥" value={stats?.todayTotal}    label="Today's Queue"   sub={`${stats?.todayPending??"—"} still pending`} color="#3B82F6" delay={0.05}/>
        <StatCard icon="✅" value={stats?.todayConsulted} label="Consulted Today" sub="OPD walk-ins"                                color={C.greenMid} delay={0.1}/>
        <StatCard icon="📋" value={stats?.rxToday}        label="Prescriptions"   sub="written today"                              color={C.gold} delay={0.15}/>
        <StatCard icon="📅" value={stats?.weekPatients}   label="This Week"       sub="patients seen"                              color="#8B5CF6" delay={0.2} onClick={() => navigate("/staff-portal/patient/search?week=1")}/>
        <StatCard icon="💻" value={stats?.onlineBookings} label="Online Bookings" sub="scheduled today"                            color={C.terra} delay={0.25}/>
      </div>

      {loading && (
        <>
          {/* Skeleton sections mirror the real Pending / In-Progress layout so
             the page geometry is stable before the first response arrives. */}
          <SkeletonSection dotColor="#BFDBFE" count={3} />
          <SkeletonSection dotColor="#FDE68A" count={1} />
        </>
      )}

      {/* Online Consultations Panel */}
      {!loading && (
        <motion.div initial={{opacity:0,y:12}} animate={{opacity:1,y:0}} transition={{delay:0.3}}
          style={{background:"#fff",borderRadius:18,boxShadow:"0 4px 20px rgba(0,0,0,0.07)",
            border:`1px solid ${C.creamD}`,overflow:"hidden"}}>

          <div style={{
            padding:"14px 20px",
            background:`linear-gradient(135deg,${C.goldBg},${C.cream})`,
            borderBottom:`1px solid ${C.goldL}30`,
            display:"flex",alignItems:"center",justifyContent:"space-between",
          }}>
            <div style={{display:"flex",alignItems:"center",gap:10}}>
              <span style={{fontSize:18}}>💻</span>
              <div>
                <div style={{fontWeight:700,fontSize:14,color:C.ink,fontFamily:"'Inter',sans-serif"}}>
                  Today's Online Consultations
                </div>
                <div style={{fontSize:11,color:C.inkLight,fontFamily:"'Inter',sans-serif"}}>
                  {onlineAppts.length > 0
                    ? `${onlineAppts.length} booking${onlineAppts.length>1?"s":""} scheduled for today`
                    : "No online bookings for today"}
                </div>
              </div>
            </div>
            <button onClick={() => setShowOnline(v => !v)} style={{
              padding:"5px 12px",borderRadius:8,border:`1px solid ${C.goldL}40`,
              background:"transparent",color:C.gold,fontWeight:600,
              fontSize:12,cursor:"pointer",fontFamily:"'Inter',sans-serif",
            }}>
              {showOnline ? "Hide ▲" : "Show ▼"}
            </button>
          </div>

          <AnimatePresence>
            {showOnline && (
              <motion.div initial={{height:0,opacity:0}} animate={{height:"auto",opacity:1}}
                exit={{height:0,opacity:0}} transition={{duration:0.25}} style={{overflow:"hidden"}}>
                <div style={{padding:"16px 20px",display:"flex",flexDirection:"column",gap:10}}>
                  {onlineAppts.length === 0 ? (
                    <div style={{textAlign:"center",padding:"24px 0",color:C.inkLight,
                      fontFamily:"'Inter',sans-serif",fontSize:13}}>
                      <div style={{fontSize:32,marginBottom:8}}>📭</div>
                      No online consultations booked for today.
                    </div>
                  ) : (
                    onlineAppts.map((appt, i) => <OnlineCard key={appt._id} appt={appt} index={i}/>)
                  )}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      )}

      {/* Empty OPD */}
      {!loading && patients.length === 0 && (
        <div style={{background:"#fff",borderRadius:18,padding:"40px 24px",textAlign:"center",
          boxShadow:"0 4px 20px rgba(0,0,0,0.07)",border:`1px solid ${C.creamD}`}}>
          <div style={{fontSize:40,marginBottom:10}}>🏥</div>
          <div style={{fontWeight:600,color:C.inkMid,fontFamily:"'Inter',sans-serif"}}>No OPD patients today yet</div>
          <div style={{fontSize:13,color:C.inkLight,marginTop:6,fontFamily:"'Inter',sans-serif"}}>
            Add a new walk-in or check in a returning patient
          </div>
          <div style={{display:"flex",gap:12,justifyContent:"center",marginTop:16}}>
            <button onClick={() => navigate("/staff-portal/patient/new")}
              style={{padding:"10px 20px",borderRadius:10,border:"none",background:C.greenMid,
                color:"#fff",fontWeight:600,fontSize:13,cursor:"pointer",fontFamily:"'Inter',sans-serif"}}>
              + New Walk-in
            </button>
            <button onClick={() => navigate("/staff-portal/patient/search")}
              style={{padding:"10px 20px",borderRadius:10,border:`1px solid ${C.creamD}`,
                background:"#fff",color:C.inkMid,fontWeight:600,fontSize:13,cursor:"pointer",fontFamily:"'Inter',sans-serif"}}>
              🔍 Returning Patient
            </button>
          </div>
        </div>
      )}

      {/* Pending */}
      {!loading && pending.length > 0 && (
        <div>
          <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:12}}>
            <span style={{width:10,height:10,borderRadius:"50%",background:"#3B82F6",display:"inline-block"}}/>
            <div style={{fontSize:11,fontWeight:700,color:"#3B82F6",letterSpacing:"0.1em",
              textTransform:"uppercase",fontFamily:"'Inter',sans-serif"}}>
              Pending ({pending.length})
            </div>
          </div>
          <div style={{display:"grid",gap:16,gridTemplateColumns:"repeat(auto-fill,minmax(260px,1fr))"}}>
            {pending.map((p,i) => (
              <PatientCard key={p._id} patient={p} index={i} status="pending"
                onConsult={() => navigate(`/staff-portal/prescription/${p.patientId}?visitType=new`)}
                onHistory={() => navigate(`/staff-portal/patient/history/${p.patientId}`)}
                onEdit={() => navigate(`/staff-portal/patient/edit/${p.patientId}`)}/>
            ))}
          </div>
        </div>
      )}

      {/* In Progress */}
      {!loading && inProgress.length > 0 && (
        <div>
          <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:12}}>
            <span style={{width:10,height:10,borderRadius:"50%",background:"#F59E0B",display:"inline-block"}}/>
            <div style={{fontSize:11,fontWeight:700,color:"#D97706",letterSpacing:"0.1em",
              textTransform:"uppercase",fontFamily:"'Inter',sans-serif"}}>
              In Progress ({inProgress.length})
            </div>
          </div>
          <div style={{display:"grid",gap:16,gridTemplateColumns:"repeat(auto-fill,minmax(260px,1fr))"}}>
            {inProgress.map((p,i) => (
              <PatientCard key={p._id} patient={p} index={i} status="in_progress"
                onConsult={() => navigate(`/staff-portal/prescription/${p.patientId}?visitType=new`)}
                onHistory={() => navigate(`/staff-portal/patient/history/${p.patientId}`)}
                onEdit={() => navigate(`/staff-portal/patient/edit/${p.patientId}`)}/>
            ))}
          </div>
        </div>
      )}

      {/* Finished */}
      {!loading && completed.length > 0 && (
        <div>
          <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:12}}>
            <span style={{width:10,height:10,borderRadius:"50%",background:C.greenMid,display:"inline-block"}}/>
            <div style={{fontSize:11,fontWeight:700,color:C.greenMid,letterSpacing:"0.1em",
              textTransform:"uppercase",fontFamily:"'Inter',sans-serif"}}>
              Finished ({completed.length})
            </div>
          </div>
          <div style={{display:"grid",gap:16,gridTemplateColumns:"repeat(auto-fill,minmax(260px,1fr))"}}>
            {completed.map((p,i) => (
              <PatientCard key={p._id} patient={p} index={i} status="completed"
                onConsult={() => navigate(`/staff-portal/prescription/${p.patientId}?visitType=new`)}
                onHistory={() => navigate(`/staff-portal/patient/history/${p.patientId}`)}
                onEdit={() => navigate(`/staff-portal/patient/edit/${p.patientId}`)}/>
            ))}
          </div>
        </div>
      )}

    </div>
  );
}